import { TransportGroupStatus } from 'core/api/transport-groups/transport-groups-api-interface';

/**
 * How a batch's state is described to a person.
 *
 * Plain words for states rather than commentary on them: an operator scanning a
 * list wants the same vocabulary the rest of the system uses, and "Up for
 * grabs" next to "Assigned" reads as two different kinds of thing.
 */
export const TransportGroupStatusLabels: Record<TransportGroupStatus, string> = {
  assigned: 'Assigned',
  posted: 'Available',
  acknowledged: 'Accepted',
  inProgress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** In the order a run moves through them. */
export const TransportGroupStatuses: TransportGroupStatus[] = [
  'posted',
  'assigned',
  'acknowledged',
  'inProgress',
  'completed',
  'cancelled',
];

/**
 * Whether a batch can still be given to a named driver.
 *
 * Only one nobody has been given. A batch that already has a driver has been
 * handed over — they have been told, and offering to hand it to somebody else
 * from a row of buttons is how work gets taken out from under a driver already
 * on their way to it. Changing your mind is cancelling and rebuilding, which is
 * a deliberate act rather than a stray click.
 */
export const canAssignBatch = (status: TransportGroupStatus): boolean => status === 'posted';

/**
 * Whether a batch can be moved to somebody else, or put back to the pool.
 *
 * Only one that has a driver who has not yet accepted it. They have been told
 * about it and said nothing, so moving it takes nothing away from them — and
 * the alternative, cancelling and rebuilding from the request queue, is a great
 * deal of work for "Dave is off sick". Once somebody has taken it on it is
 * theirs, and moving it underneath them is how two drivers arrive at the same
 * ward for the same box.
 */
export const canReassignBatch = (status: TransportGroupStatus): boolean => status === 'assigned';

/**
 * Whether a batch can still be called off.
 *
 * Up to the moment a driver takes it on, including while it is assigned: they
 * have been told about it but have not said yes, so nothing is being pulled
 * away from anybody.
 */
export const canCancelBatch = (status: TransportGroupStatus): boolean =>
  status === 'posted' || status === 'assigned';
